/**
 * Login request mapping helpers (infrastructure).
 * Domain types: `@/domain/auth/login-request`.
 */

import type { LoginRequestDto } from "@/domain/auth/auth.api.types";
import {
  LOGIN_MODE,
  type LoginIdentifierPrefill,
  type LoginMode,
  type LoginRequest,
} from "@/domain/auth/login-request";
import {
  countries,
  findCountryByLongestDialPrefix,
  formatDialCodePrefix,
  getCountryByIso2,
  toDialCodeDigits,
  type CountryType,
} from "@/utils/country-codes";
import {
  buildE164Phone,
  isValidEmail,
  isValidE164Phone,
  normalizeEmailInput,
  normalizeMobileForSubmit,
  normalizeNationalNumber,
  parseStoredPhoneNumber,
  stripControlChars,
} from "@/utils/signup-input";

/**
 * Map typed client login → existing wire DTO.
 * Single compatibility adapter for POST /auth/v1/token.
 */
export function toLoginWireDto(request: LoginRequest): LoginRequestDto {
  if (request.loginType === LOGIN_MODE.EMAIL) {
    return {
      emailOrMobile: normalizeEmailInput(request.email).toLowerCase(),
      password: request.password,
    };
  }
  return {
    emailOrMobile: request.e164PhoneNumber,
    password: request.password,
  };
}

export function formatLoginCountryCode(dialPhone: string | null | undefined): string {
  return formatDialCodePrefix(dialPhone);
}

/**
 * Build a typed LoginRequest from form values.
 * Returns null when mobile cannot form a valid E.164 number.
 */
export function buildLoginRequest(args: {
  loginMode: LoginMode;
  email: string;
  country: CountryType | null | undefined;
  nationalNumber: string;
  password: string;
}): LoginRequest | null {
  const password = String(args.password ?? "");
  if (args.loginMode === LOGIN_MODE.EMAIL) {
    return {
      loginType: LOGIN_MODE.EMAIL,
      email: normalizeEmailInput(args.email).toLowerCase(),
      password,
    };
  }
  const dial = args.country?.phone ?? "";
  const national = normalizeNationalNumber(args.nationalNumber, dial);
  const e164PhoneNumber = buildE164Phone(dial, national);
  if (!e164PhoneNumber) return null;
  return {
    loginType: LOGIN_MODE.MOBILE,
    countryCode: formatLoginCountryCode(dial),
    nationalNumber: national,
    e164PhoneNumber,
    password,
  };
}

/**
 * Prefill from `?identifier=` (post-signup recovery).
 * Email → email mode; `+…` E.164 → mobile + inferred country; otherwise leave defaults.
 */
export function parseLoginIdentifierPrefill(
  identifier: string | null | undefined,
  fallbackCountryIso2: string | null | undefined = "GB"
): LoginIdentifierPrefill {
  const fallback =
    getCountryByIso2(fallbackCountryIso2) ??
    getCountryByIso2("GB") ??
    countries[0];

  const empty: LoginIdentifierPrefill = {
    mode: LOGIN_MODE.EMAIL,
    email: "",
    countryIso2: fallback.code,
    nationalNumber: "",
  };

  const raw = stripControlChars(String(identifier ?? "")).trim();
  if (!raw) return empty;

  if (raw.includes("@")) {
    return {
      ...empty,
      mode: LOGIN_MODE.EMAIL,
      email: normalizeEmailInput(raw),
    };
  }

  const mobileNormalized = normalizeMobileForSubmit(raw);
  if (!mobileNormalized.startsWith("+")) {
    return empty;
  }

  if (!isValidE164Phone(mobileNormalized) && !/^\+[1-9]\d{4,14}$/.test(mobileNormalized)) {
    return empty;
  }

  const digits = mobileNormalized.slice(1);
  const matched = findCountryByLongestDialPrefix(digits);
  const country = matched ?? fallback;
  const dial = toDialCodeDigits(country.phone);
  const national =
    dial && digits.startsWith(dial) ? digits.slice(dial.length) : digits;

  return {
    mode: LOGIN_MODE.MOBILE,
    email: "",
    countryIso2: country.code,
    nationalNumber: national,
  };
}

/**
 * Recovery / silent login from a stored identifier (email or E.164 mobile).
 */
export function loginRequestFromIdentifier(
  identifier: string,
  password: string
): LoginRequest {
  const raw = stripControlChars(String(identifier ?? "")).trim();
  if (raw.includes("@") || isValidEmail(raw)) {
    return {
      loginType: LOGIN_MODE.EMAIL,
      email: normalizeEmailInput(raw).toLowerCase(),
      password,
    };
  }

  let e164 = normalizeMobileForSubmit(raw);
  if (e164 && !e164.startsWith("+")) {
    e164 = `+${e164}`;
  }
  const parsed = parseStoredPhoneNumber(e164);
  const dial = parsed.country?.phone ?? "";
  const national = parsed.nationalNumber;
  const built = buildE164Phone(dial, national);
  const e164PhoneNumber = built || (isValidE164Phone(e164) ? e164 : e164);

  return {
    loginType: LOGIN_MODE.MOBILE,
    countryCode: formatLoginCountryCode(dial),
    nationalNumber: national,
    e164PhoneNumber,
    password,
  };
}
